import { Col, DatePicker, Form, Input, Row, Select } from "antd";
import type { Dayjs } from "dayjs";
import { OrganizationTreeSelect } from "@/modules/organization-cross-access/components/OrganizationTreeSelect";
import { useTaskTypes } from "@/modules/task-types/hooks/useTaskTypes";
import { CREATE_TASK_STATUS_OPTIONS, TASK_PRIORITY_OPTIONS } from "../constants";
import type { CreateTaskStatus, TaskPriority, TaskTargetType } from "../types";
import type { TaskLocationDraft } from "./TaskLocationListInput";
import { TaskTargetFields } from "./TaskTargetFields";

// AntD form values: dueDate comes back as Dayjs, locations as in-progress
// drafts (TaskLocationListInput's own shape) — both converted at submit time.
export interface TaskFormValues {
	title: string;
	organizationId: string;
	typeId: string;
	priority?: TaskPriority;
	dueDate?: Dayjs | null;
	status?: CreateTaskStatus;
	brigadeId: string;
	targetType: TaskTargetType;
	locations?: TaskLocationDraft[];
	plantingIds?: string[];
	description: string;
}

interface TaskFormProps {
	form: ReturnType<typeof Form.useForm<TaskFormValues>>[0];
	/** "urgent": priority fixed to URGENT. */
	mode?: "default" | "urgent";
}

export function TaskForm({ form, mode = "default" }: TaskFormProps) {
	const isUrgent = mode === "urgent";
	const selectedOrgId = Form.useWatch("organizationId", form);

	const { data: taskTypesData } = useTaskTypes({ page: 1, limit: 10 });
	const taskTypes = taskTypesData?.data?.items ?? [];

	return (
		<Form
			form={form}
			layout="vertical"
			requiredMark={false}
			className="mt-4"
			initialValues={{ priority: isUrgent ? "URGENT" : "MEDIUM", status: "PENDING" }}
		>
			<Row gutter={16}>
				<Col span={isUrgent ? 24 : 16}>
					<Form.Item
						name="title"
						label="Sarlavha"
						rules={[
							{ required: true, message: "Sarlavhani kiriting" },
							{ max: 255, message: "Maksimum 255 ta belgi" },
						]}
					>
						<Input placeholder="Masalan: Ko'chani supirish" id="task-title" />
					</Form.Item>
				</Col>
				{!isUrgent && (
					<Col span={8}>
						<Form.Item
							name="status"
							label="Holat"
							tooltip="Qoralama sifatida saqlangan topshiriq 'Kutilmoqda' holatiga o'tkazilmaguncha brigadaga ko'rinmaydi"
						>
							<Select options={CREATE_TASK_STATUS_OPTIONS} id="task-status" />
						</Form.Item>
					</Col>
				)}
			</Row>
			<Row gutter={16}>
				<Col span={12}>
					<Form.Item
						name="organizationId"
						label="Tashkilot"
						rules={[{ required: true, message: "Tashkilotni tanlang" }]}
					>
						<OrganizationTreeSelect placeholder="Tashkilotni tanlang" />
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item
						name="typeId"
						label="Topshiriq turi"
						rules={[{ required: true, message: "Topshiriq turini tanlang" }]}
					>
						<Select
							placeholder="Topshiriq turini tanlang"
							id="task-type"
							showSearch
							optionFilterProp="label"
							options={taskTypes.map((t) => ({ value: t.id, label: t.name }))}
						/>
					</Form.Item>
				</Col>
			</Row>
			<Row gutter={16}>
				<Col span={12}>
					<Form.Item name="priority" label="Ustuvorlik">
						<Select
							options={TASK_PRIORITY_OPTIONS}
							placeholder="Tanlang"
							id="task-priority"
							disabled={isUrgent}
						/>
					</Form.Item>
				</Col>
				<Col span={12}>
					<Form.Item name="dueDate" label="Muddat">
						<DatePicker className="w-full" format="DD.MM.YYYY" id="task-dueDate" />
					</Form.Item>
				</Col>
			</Row>

			<TaskTargetFields form={form} organizationId={selectedOrgId} />

			<Row gutter={16}>
				<Col span={24}>
					<Form.Item
						name="description"
						label="Izoh"
						rules={[
							{ required: true, message: "Izohni kiriting" },
							{ max: 2000, message: "Maksimum 2000 ta belgi" },
						]}
					>
						<Input.TextArea rows={3} placeholder="Qisqacha izoh" id="task-description" />
					</Form.Item>
				</Col>
			</Row>
		</Form>
	);
}
